import { Text } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSearch } from '@/hooks/useSearch';
import { UserApi } from '@/utilities/api/UserApi';
import { Autocomplete, AutocompleteValue, Stack, TextField } from '@mui/material';
import { UserType } from '@shared/enums';
import { getAccountName } from '@shared/utilities';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';

export interface CustomerOption {
  externalId?: string | null,
  id: string,
  label: string,
}

type NamedCustomer = Parameters<typeof getAccountName>[0] & { id: string };

/** One customer, or a list of them when `multiple` is set. */
type CustomerValue<Multiple extends boolean | undefined> = AutocompleteValue<
  CustomerOption,
  Multiple,
  false,
  false
>;

const pageSize = 20;
// How close to the end of the list the next page loads.
const scrollLoadPx = 40;

export const toCustomerOption = (customer: NamedCustomer): CustomerOption => ({
  externalId: customer.externalId,
  id: customer.id,
  label: getAccountName(customer),
});

const toSelectedList = (value: CustomerOption | CustomerOption[] | null): CustomerOption[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return value ? [value] : [];
};

interface CustomerAutocompleteProps<Multiple extends boolean | undefined> {
  label: string,
  /** With `multiple`, chips past this many collapse into a "+N" count. */
  limitTags?: number,
  multiple?: Multiple,
  onChange: (value: CustomerValue<Multiple>) => void,
  /** Customers belong to a retailer, so the picker stays disabled until one is chosen. */
  retailerId: string,
  testID: string,
  value: CustomerValue<Multiple>,
  width?: number,
}

const RetailerCustomerAutocomplete = <Multiple extends boolean | undefined>({
  label,
  limitTags,
  multiple,
  onChange,
  retailerId,
  testID,
  value,
  width = 300,
}: CustomerAutocompleteProps<Multiple>) => {
  const { t } = useTranslation();
  const { debouncedSearch, setSearch } = useSearch();

  const {
    data: customers,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery(
    [QueryKeys.LIST_USERS, 'customer-autocomplete', retailerId, debouncedSearch],
    ({ pageParam = 0 }) => UserApi.list({
      limit: pageSize,
      page: pageParam,
      retailerId,
      search: debouncedSearch,
      userType: [UserType.Farmer],
    }),
    {
      enabled: !!retailerId,
      getNextPageParam: (lastPage) => (
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined
      ),
      keepPreviousData: true,
    },
  );

  const loadedOptions = (customers?.pages.flatMap(({ data }) => data) ?? []).map(toCustomerOption);
  // MUI warns when a value is missing from the options, as it is when set from outside.
  const selectedNotLoaded = toSelectedList(value)
    .filter((selected) => !loadedOptions.some(({ id }) => id === selected.id));
  const options = [...selectedNotLoaded, ...loadedOptions];

  const loadMoreNearBottom = (event: React.UIEvent<HTMLElement>) => {
    const list = event.currentTarget;
    const isNearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - scrollLoadPx;
    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  return (
    <Autocomplete<CustomerOption, Multiple>
      ListboxProps={{ onScroll: loadMoreNearBottom }}
      disableCloseOnSelect={!!multiple}
      disabled={!retailerId}
      filterOptions={(unfiltered) => unfiltered}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      limitTags={limitTags}
      loading={isFetching}
      multiple={multiple}
      noOptionsText={t($ => $.customerAutocomplete.noMatches)}
      onChange={(_, chosen) => {
        // MUI resets the input after a pick, as a 'reset' that is not a search, so drop the
        // typed search too rather than leave it filtering the list unseen.
        setSearch('');
        onChange(chosen);
      }}
      onInputChange={(_, search, reason) => {
        if (reason !== 'reset') {
          setSearch(search);
        }
      }}
      options={options}
      renderInput={(params) => (
        <TextField {...params} data-testid={testID} label={label} />
      )}
      // Keyed by id, with the ERP id below, since several customers can share a name.
      renderOption={(props, customer) => (
        <li {...props} key={customer.id}>
          <Stack minWidth={0}>
            <Text category="body-medium" noWrap>{customer.label}</Text>
            <Text category="body-small" noWrap sx={{ color: 'text.secondary' }}>
              {customer.externalId ?? customer.id}
            </Text>
          </Stack>
        </li>
      )}
      sx={{ minWidth: width }}
      value={value}
    />
  );
};

/**
 * A retailer's customers, as the customer points report lists them, loaded as the list scrolls.
 * It remounts for each retailer, so a search typed for one never filters the next one's list.
 */
export const CustomerAutocomplete = <Multiple extends boolean | undefined = false>(
  props: CustomerAutocompleteProps<Multiple>,
) => <RetailerCustomerAutocomplete key={props.retailerId} {...props} />;
